import { canAnimateElement, readCssTimeMs } from '../../components/motion-policy.js';
import { cancelAnimations, playAnimation, settleAnimations } from '../../components/motion-runner.js';
import { bindNoteContentByteLimit } from './content-limit.js';

/**
 * Owns the edit mode of rendered note items: the edit form's visibility, the read-only content it
 * replaces and the toggle's `aria-expanded`. Items arrive later through the notes stream, so every
 * lookup goes through delegation from the section. Saving is the form's ordinary POST.
 * The state flips at once; the swap is then played from the old height and side to the new one, so
 * the notes below follow the body's height.
 */
export function initGameDetailsNoteEdit(documentRef = document) {
    const notesSection = documentRef.querySelector('[data-game-notes-section]');
    if (!(notesSection instanceof HTMLElement)) {
        return;
    }

    const boundTextareas = new WeakSet();
    const morphRuns = new WeakMap();
    let openItem = null;

    const partsOf = (item) => {
        const form = item.querySelector('[data-game-note-edit-form]');
        const textarea = form instanceof HTMLElement ? form.querySelector('textarea') : null;
        const toggle = item.querySelector('[data-game-note-edit-toggle]');
        if (
            !(form instanceof HTMLFormElement)
            || !(textarea instanceof HTMLTextAreaElement)
            || !(toggle instanceof HTMLButtonElement)
        ) {
            return null;
        }

        return {
            form,
            textarea,
            toggle,
            body: item.querySelector('[data-game-note-body]'),
            content: item.querySelector('[data-game-note-content]'),
        };
    };

    const applyEditing = (parts, editing) => {
        parts.form.hidden = !editing;
        if (parts.content instanceof HTMLElement) {
            parts.content.hidden = editing;
        }
        parts.toggle.setAttribute('aria-expanded', String(editing));
    };

    const finishMorph = (run) => {
        cancelAnimations(run.animations);
        run.body.classList.remove('game-note-body-morphing');
        if (run.leaving !== null) {
            run.leaving.classList.remove('game-note-body-leaving');
            run.leaving.inert = false;
            run.leaving.hidden = true;
        }
    };

    const setEditing = (parts, editing) => {
        const body = parts.body instanceof HTMLElement ? parts.body : null;
        const animated = body !== null && canAnimateElement(body, documentRef.defaultView);
        // Measured before an interrupted run is settled, so a reversal starts where the eye is.
        const startHeight = animated ? body.getBoundingClientRect().height : 0;
        // Settled even when this swap is not animated, or its late callback would hide the new side.
        const previousRun = body === null ? undefined : morphRuns.get(body);
        if (previousRun !== undefined) {
            morphRuns.delete(body);
            finishMorph(previousRun);
        }

        applyEditing(parts, editing);
        if (!animated) {
            return;
        }

        const endHeight = body.getBoundingClientRect().height;
        const content = parts.content instanceof HTMLElement ? parts.content : null;
        const leaving = editing ? content : parts.form;
        const entering = editing ? parts.form : content;

        body.classList.add('game-note-body-morphing');
        if (leaving !== null) {
            // Shown only to fade out: inert keeps Tab and clicks out of the side already closed.
            leaving.inert = true;
            leaving.hidden = false;
            leaving.classList.add('game-note-body-leaving');
        }

        const duration = readCssTimeMs(body, '--game-note-edit-morph-duration', 320);
        const easing = 'cubic-bezier(0.22, 1, 0.36, 1)';
        const run = {
            body,
            leaving,
            animations: [
                playAnimation(body, [
                    { height: `${startHeight}px` },
                    { height: `${endHeight}px` },
                ], { duration, easing }),
                leaving === null ? null : playAnimation(leaving, [
                    { opacity: 1, transform: 'translateY(0)' },
                    { opacity: 0, transform: 'translateY(-4px)' },
                ], { duration: Math.min(duration, 220), easing, fill: 'forwards' }),
                entering === null ? null : playAnimation(entering, [
                    { opacity: 0, transform: 'translateY(8px)' },
                    { opacity: 1, transform: 'translateY(0)' },
                ], { duration, easing }),
            ].filter(Boolean),
        };
        morphRuns.set(body, run);

        void settleAnimations(run.animations).then(() => {
            if (morphRuns.get(body) !== run) {
                return;
            }

            morphRuns.delete(body);
            finishMorph(run);
        });
    };

    const close = (item, { restoreFocus }) => {
        const parts = partsOf(item);
        if (openItem === item) {
            openItem = null;
        }
        if (parts === null) {
            return;
        }

        parts.textarea.value = parts.textarea.defaultValue;
        parts.textarea.setCustomValidity('');
        setEditing(parts, false);
        if (restoreFocus) {
            parts.toggle.focus();
        }
    };

    const open = (item) => {
        const parts = partsOf(item);
        if (parts === null) {
            return;
        }
        if (openItem !== null && openItem !== item) {
            close(openItem, { restoreFocus: false });
        }

        if (!boundTextareas.has(parts.textarea)) {
            boundTextareas.add(parts.textarea);
            bindNoteContentByteLimit(parts.textarea);
        }
        setEditing(parts, true);
        openItem = item;
        parts.textarea.focus();
        parts.textarea.setSelectionRange(parts.textarea.value.length, parts.textarea.value.length);
    };

    notesSection.addEventListener('click', (event) => {
        const target = event.target;
        if (!(target instanceof HTMLElement)) {
            return;
        }

        const item = target.closest('[data-note-id]');
        if (!(item instanceof HTMLElement)) {
            return;
        }

        if (target.closest('[data-game-note-edit-toggle]') !== null) {
            open(item);
        } else if (target.closest('[data-game-note-edit-cancel]') !== null) {
            close(item, { restoreFocus: true });
        }
    });
}
